import React, { useState, useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import '../../styles/components/itinerary/PlaceWidget.css';
import { getReadableTypeName, getTypesToIgnore } from '../../utils/PlaceTypeMapping';
import { SingleStarRating } from '../common/StarRating';
import { usePlaceContext } from './PlaceContext';
import { scrollHorizontallyOnTouchMove } from './drag_and_drop/Handlers';
import { MapContext } from '../maps/MapContext';
import useApi from '../../Api';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../AuthContext';
import { fetchPlaceDetails } from '../map_interaction/PlaceDetailsService';

const PlaceWidget = React.memo(({ place, columnId, placeIndex, onRemove, setDragData, onNotesChange, readOnly }) => {
  const { showPlaceInfoBox, updatePlaceDetails } = usePlaceContext();
  const { googleMapsAPI } = useContext(MapContext);
  const { token } = useAuth();
  const apiClient = useApi();
  const [localPlace, setLocalPlace] = useState(place);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const ghostRef = useRef(null);
  const widgetRef = useRef(null);
  const observerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, { threshold: 0.1 });
    if (widgetRef.current) {
      observerRef.current.observe(widgetRef.current);
    }
    return () => {
      if (observerRef.current && widgetRef.current) {
        observerRef.current.unobserve(widgetRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible && !localPlace.__detailsFetched && localPlace.google_place_id && googleMapsAPI) {
      const loadDetails = async () => {
        try {
          const details = await fetchPlaceDetails(googleMapsAPI, localPlace.google_place_id, apiClient);
          if (details) {
            const updatedPlace = { ...localPlace, ...details, __detailsFetched: true };
            setLocalPlace(updatedPlace);
            updatePlaceDetails(updatedPlace);
          }
        } catch (e) {
          console.error("Failed to load place details:", e);
        }
      };
      loadDetails();
    }
  }, [isVisible, localPlace, googleMapsAPI, apiClient, updatePlaceDetails]);

  useEffect(() => {
    setLocalPlace(prev => ({ ...prev, notes: place.notes, date: place.date, time: place.time }));
  }, [place.notes, place.date, place.time]);

  useEffect(() => {
    const handleCustomPlaceUpdate = (event) => {
      const updatedPlace = event.detail;
      const widgetId = localPlace.custom_place_id || localPlace.id;
      if ((updatedPlace.custom_place_id && updatedPlace.custom_place_id === widgetId) ||
          (!updatedPlace.custom_place_id && updatedPlace.id === widgetId)) {
        setLocalPlace(updatedPlace);
      }
    };
    window.addEventListener('customPlaceUpdated', handleCustomPlaceUpdate);
    return () => {
      window.removeEventListener('customPlaceUpdated', handleCustomPlaceUpdate);
    };
  }, [localPlace.custom_place_id, localPlace.id]);

  const handleClick = () => {
    showPlaceInfoBox({
      ...localPlace,
      showExtension: true,
      notes: localPlace.notes || '',
      lat: localPlace.latitude,
      lng: localPlace.longitude,
      columnId: columnId,
      position: placeIndex,
      fromWidget: true
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleDragStart = (e) => {
    if (readOnly) return;
    setDragData(e, localPlace, columnId, placeIndex);
    e.currentTarget.classList.add('dragging');
    document.querySelectorAll('.scroll-bar').forEach(bar => bar.classList.add('taller'));
  };

  const handleDragEnd = (e) => {
    if (readOnly) return;
    e.currentTarget.classList.remove('dragging');
    document.querySelectorAll('.scroll-bar').forEach(bar => bar.classList.remove('taller'));
  };

  const handleRemove = (e) => {
    if (readOnly) return;
    e.stopPropagation();
    onRemove(columnId, localPlace.id);
  };

  const handleImageDragStart = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const widget = e.target.closest('.place-widget');
    widget.draggable = true;
    widget.dispatchEvent(new DragEvent('dragstart', { dataTransfer: e.dataTransfer }));
  };

  const createGhostElement = (touch) => {
    const widgetEl = touch.target.closest('.place-widget');
    if (!widgetEl) return;
    const clone = widgetEl.cloneNode(true);
    clone.style.position = 'fixed';
    clone.style.top = `${touch.clientY}px`;
    clone.style.left = `${touch.clientX}px`;
    clone.style.width = `${widgetEl.offsetWidth}px`;
    clone.style.height = `${widgetEl.offsetHeight}px`;
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '0.7';
    clone.style.zIndex = '9999';
    document.body.appendChild(clone);
    ghostRef.current = clone;
  };

  const moveGhostElement = (touch) => {
    if (ghostRef.current) {
      ghostRef.current.style.top = `${touch.clientY}px`;
      ghostRef.current.style.left = `${touch.clientX}px`;
    }
  };

  const removeGhostElement = () => {
    if (ghostRef.current && ghostRef.current.parentNode) {
      ghostRef.current.parentNode.removeChild(ghostRef.current);
      ghostRef.current = null;
    }
  };

  const handleTouchStart = (e) => {
    if (readOnly) return;
    const data = {
      placeId: localPlace.id,
      columnId,
      placeIndex,
      placeData: localPlace,
      photoUrl: localPlace.photoUrl,
    };
    window.__dragData = JSON.stringify(data);
    setIsTouchDragging(true);
    e.currentTarget.classList.add('dragging');
    document.querySelectorAll('.scroll-bar').forEach(bar => bar.classList.add('taller'));
    const touch = e.touches[0];
    createGhostElement(touch);
  };

  const handleTouchMove = (e) => {
    if (readOnly || !isTouchDragging) return;
    const touch = e.touches[0];
    moveGhostElement(touch);
    scrollHorizontallyOnTouchMove(touch.clientX);
    const targetEl = document.elementFromPoint(touch.clientX, touch.clientY);
    if (targetEl) {
      try {
        const dragOverEvent = new DragEvent('dragover', { bubbles: true });
        Object.defineProperty(dragOverEvent, 'dataTransfer', { value: { getData: () => window.__dragData || '' } });
        targetEl.dispatchEvent(dragOverEvent);
      } catch (err) {}
    }
  };

  const handleTouchEnd = (e) => {
    if (readOnly || !isTouchDragging) return;
    setIsTouchDragging(false);
    e.currentTarget.classList.remove('dragging');
    document.querySelectorAll('.scroll-bar').forEach(bar => bar.classList.remove('taller'));
    removeGhostElement();
    const touch = e.changedTouches[0];
    const dropTarget = document.elementFromPoint(touch.clientX, touch.clientY);
    if (dropTarget) {
      try {
        const dropEvent = new DragEvent('drop', { bubbles: true });
        Object.defineProperty(dropEvent, 'dataTransfer', { value: { getData: () => window.__dragData || '' } });
        dropTarget.dispatchEvent(dropEvent);
      } catch (err) {}
    }
    window.__dragData = null;
  };

  const getPlaceImageUrl = (p) => {
    if (p.photoUrl) {
      return p.photoUrl;
    } else if (p.photos && p.photos.length > 0) {
      return p.photos[0].url;
    }
    return null;
  };

  const typesToIgnore = getTypesToIgnore();
  const formatTypes = (types) =>
    !types || types.length === 0
      ? ''
      : types.filter(type => !typesToIgnore.includes(type))
             .map(type => getReadableTypeName(type))
             .filter(Boolean);
  const formattedTypes = formatTypes(localPlace.types);
  const formatDate = (date) => date ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}` : '';
  const formatTime = (time) => new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const displayDateTime = () => {
    const date = localPlace.date ? formatDate(new Date(localPlace.date)) : '';
    const time = localPlace.time ? formatTime(new Date(localPlace.time)) : '';
    return date && time ? `${time} - ${date}` : date || time || 'Click to edit';
  };
  const displayLocation = () =>
    localPlace.location ||
    (localPlace.city && localPlace.country
      ? `${localPlace.city}, ${localPlace.country}`
      : localPlace.city || localPlace.country || 'Custom Place');

  const handleNavigate = () => {
    if (localPlace.explore_origin_id) {
    } else {
      console.error('Origin ID is not available');
    }
  };

  return (
    <div className="place-widget-container" ref={widgetRef}>
      <div className="place-widget"
           draggable={readOnly ? false : true}
           onClick={handleClick}
           onKeyDown={handleKeyDown}
           tabIndex="0"
           {...(!readOnly && {
             onDragStart: handleDragStart,
             onDragEnd: handleDragEnd,
             onTouchStart: handleTouchStart,
             onTouchMove: handleTouchMove,
             onTouchEnd: handleTouchEnd
           })}
           aria-label={`Place widget for ${localPlace.name}`}
           role="listitem"
           aria-grabbed={readOnly ? "false" : "true"}
           aria-dropeffect={readOnly ? "none" : "move"}>
        { getPlaceImageUrl(localPlace) && (
          <img
            src={getPlaceImageUrl(localPlace)}
            alt={`Image of ${localPlace.name}`}
            className="place-widget-image"
            onDragStart={handleImageDragStart}
            draggable={false}
          />
        )}
        <div className="place-widget-info">
          <h3>{localPlace.name}</h3>
          <div className="place-widget-location">
            <span>{displayLocation()}</span>
          </div>
          {(localPlace.about || formattedTypes) && (
            <div className="place-widget-types">{formattedTypes}</div>
          )}
          {localPlace.rating !== null && localPlace.rating !== undefined && (
            <div className="place-widget-rating">
              <SingleStarRating rating={localPlace.rating} />
            </div>
          )}
          <div className="place-widget-note">{displayDateTime()}</div>
        </div>
        {!readOnly && (
          <button className="remove-widget-button" onClick={handleRemove} aria-label={`Remove widget for ${localPlace.name}`}>
            <span className="remove-widget-icon">×</span>
          </button>
        )}
      </div>
    </div>
  );
});

PlaceWidget.propTypes = {
  place: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    location: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    rating: PropTypes.number,
    photoUrl: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string,
      filename: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
      size_mb: PropTypes.number,
      html_attributions: PropTypes.arrayOf(PropTypes.string)
    })),
    types: PropTypes.arrayOf(PropTypes.string),
    about: PropTypes.string,
    userRatingsTotal: PropTypes.number,
    formattedAddress: PropTypes.string,
    openingHours: PropTypes.arrayOf(PropTypes.string),
    website: PropTypes.string,
    priceLevel: PropTypes.number,
    businessStatus: PropTypes.string,
    adrAddress: PropTypes.string,
    plusCode: PropTypes.string,
    notes: PropTypes.string,
    date: PropTypes.instanceOf(Date),
    time: PropTypes.instanceOf(Date),
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    google_place_id: PropTypes.string,
    custom_place_id: PropTypes.string,
  }).isRequired,
  columnId: PropTypes.string,
  placeIndex: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  setDragData: PropTypes.func.isRequired,
  onNotesChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default PlaceWidget;
